/**
 * Der Deep-Link `pawbot://anruf?callId=…` — Vespera ruft an. (Ticket 2728, Stufe 1)
 *
 * Tippt Martin auf die Meldung „Vespera ruft an“, öffnet die App diese
 * Adresse. Hier steht, was dann geschieht — als eigene Datei, damit es ohne
 * die ganze App prüfbar ist (tests/frontend/anruf-link-2728.test.ts).
 *
 * ⚠️ Der Link trägt NUR die callId (32 Hex-Zeichen), nie den Anlass. Ob sie
 *    gültig ist, entscheidet allein der Server (/api/token: einmalig, nicht
 *    abgelaufen, eigenes Konto). Hier wird nur die FORM geprüft — alles andere
 *    ist ein ruhiger Hinweis, kein Absturz.
 */
import { HOECHSTDAUER_MINUTEN } from "../../shared/anruf-grenzen.js";

/** Wie der Token-Abruf einen verbrauchten/abgelaufenen/fremden Anruf meldet. */
export const ANRUF_VORBEI = "ANRUF_VORBEI";

/** Die Höchstdauer hat den Anruf beendet; ein Wiedereinwählen wird abgewiesen. */
export const ANRUF_HOECHSTDAUER = "ANRUF_HOECHSTDAUER";
export const ANRUF_HOECHSTDAUER_TEXT = `Der Anruf hat die Höchstdauer von ${HOECHSTDAUER_MINUTEN} Minuten erreicht. Du kannst gleich neu anrufen.`;

export const ANRUF_NICHT_MEHR_GUELTIG =
  "Dieser Anruf ist nicht mehr gültig. Sieh in der Glocke nach, worum es ging.";

const CALL_ID_FORM = /^[0-9a-f]{32}$/;

/** Die callId aus `pawbot://anruf?callId=…` — oder null, wenn Form oder Link nicht passen. */
export function anrufIdAusLink(raw: string): string | null {
  const m = /^pawbot:\/\/anruf\?(.*)$/i.exec(raw.trim());
  if (!m) return null;
  const id = new URLSearchParams(m[1]).get("callId");
  return id && CALL_ID_FORM.test(id) ? id : null;
}

/**
 * Der Link zu einer callId — derselbe, den die Meldung trägt
 * (server/anruf-meldungen.ts, anrufAdresse). Die Karte der Seite
 * (lib/anruf-karte.ts) hebt damit über genau diesen Weg ab.
 */
export function anrufLink(callId: string): string {
  return `pawbot://anruf?callId=${callId}`;
}

/** Was der Link an der App braucht — in app.ts aus den echten Stücken gebaut. */
export interface AnrufLinkWeg {
  /** Nur das Hauptkonto darf anrufen (wie der Dock-Knopf). */
  darfAnrufen: () => boolean;
  verbunden: () => boolean;
  verbindet: () => boolean;
  /** Zur Chat-Ansicht wechseln (handleConnect täte es sonst statt zu verbinden). */
  zumChat: () => void;
  /** Die callId für den nächsten Token-Abruf merken (null = vergessen). */
  setzeAnrufId: (id: string | null) => void;
  /** Dieselbe Folge wie der Tipp auf den Anruf-Knopf; false = ging nicht. */
  starte: () => boolean;
  /** Ruhiger Hinweis. */
  melde: (text: string) => void;
  /**
   * Stufe 2 (02.10.2026): wurde DIESE Kennung schon behandelt? Beim Abheben
   * über CallKit reicht die App den Link nativ weiter; beim Kaltstart kommt er
   * dann zweimal an (zurückgehaltenes `appUrlOpen` UND `getLaunchUrl`), und
   * nach einem Neuladen liefert `getLaunchUrl` ihn erneut. Der zweite Aufruf
   * bleibt still. Fehlt = kein Schutz (wie vorher).
   */
  schonBehandelt?: (id: string) => boolean;
  merkeBehandelt?: (id: string) => void;
}

type AnrufLinkErgebnis =
  | "gestartet"
  | "doppelt"
  | "nicht-erlaubt"
  | "ungueltig"
  | "schon-im-gespraech"
  | "nicht-gestartet";

export function behandleAnrufLink(raw: string, weg: AnrufLinkWeg): AnrufLinkErgebnis {
  if (!weg.darfAnrufen()) return "nicht-erlaubt";
  const id = anrufIdAusLink(raw);
  if (!id) {
    weg.melde(ANRUF_NICHT_MEHR_GUELTIG);
    return "ungueltig";
  }
  // Derselbe Anruf zum zweiten Mal (Kaltstart, Neuladen): still, KEIN Hinweis
  // „schon im Gespräch“ und kein zweiter Token-Abruf mit verbrauchter Kennung.
  if (weg.schonBehandelt?.(id)) return "doppelt";
  // Läuft schon ein Gespräch, wird die Kennung NICHT gemerkt (sie bliebe
  // stehen und würfe beim nächsten Anruf einen Fehler).
  if (weg.verbunden() || weg.verbindet()) {
    weg.melde("Du bist schon in einem Gespräch.");
    return "schon-im-gespraech";
  }
  weg.merkeBehandelt?.(id);
  weg.zumChat();
  weg.setzeAnrufId(id);
  if (!weg.starte()) {
    weg.setzeAnrufId(null);
    weg.melde(ANRUF_NICHT_MEHR_GUELTIG);
    return "nicht-gestartet";
  }
  return "gestartet";
}

/** Wo die zuletzt behandelte Kennung liegt (eine genügt: es klingelt nie zweimal zugleich). */
export const ANRUF_ZULETZT_SCHLUESSEL = "pawbot:anruf:zuletzt";

/** `schonBehandelt`/`merkeBehandelt` über einen Speicher (localStorage); ohne Speicher kein Schutz. */
export function behandeltSpeicher(speicher: () => Storage | null): Pick<
  AnrufLinkWeg,
  "schonBehandelt" | "merkeBehandelt"
> {
  return {
    schonBehandelt: (id) => {
      try {
        return speicher()?.getItem(ANRUF_ZULETZT_SCHLUESSEL) === id;
      } catch {
        return false;
      }
    },
    merkeBehandelt: (id) => {
      try {
        speicher()?.setItem(ANRUF_ZULETZT_SCHLUESSEL, id);
      } catch {
        /* privater Modus o. Ä.: dann eben ohne Schutz */
      }
    },
  };
}
